import { cacheDecorator } from '@ds/swymkit/tools'

import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import { createExaleadQueryParameters } from '~/features-swym/widgets/content-reader/utils/content-reader-query'

import { shouldLimitExaleadResults } from '~/features-shared/content/api/exalead-stats'
import type { IExaleadParams } from '~/features-shared/content/api/interfaces/params'
import {
  ORDERBY,
  ORDERFIELD,
} from '~/features-shared/content/api/interfaces/params'
import { contentsParser } from '~/features-shared/content/api/parser-exalead'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { SearchOneElementExaleadOptions } from '~/features-shared/content/interfaces/exalead-contents'
import type { IEntity } from '~/features-shared/libs/interfaces/entity'

import type { Parser } from '~/features-generic/services/interfaces/exalead-parser'
import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

import type { ContentExaleadServices, ExaleadService } from '~/interfaces/crud-service'
import type { ExaleadResponse } from '~/interfaces/services-dto'

interface ServiceOptions<T extends IEntity> {
  contentType?: string
  parser: Parser<T>
  api: string
}

function getService<T extends IEntity>({ contentType, parser, api }: ServiceOptions<T>): ExaleadService<T> {
  return {
    async count(params) {
      const response = await swymFetch
        .post(api, {
          json: {
            params: {
              legacyFormat: false,
              ...(contentType ? { contentType } : {}),
              query: '#all',
              with_synthesis: true,
              order_by: ORDERBY.DESC,
              order_field: ORDERFIELD.UPDATE,
              start: 0,
              nresults: 0,
              ...params,
            } as IExaleadParams,
          },
        })
        .json<ExaleadResponse>()

      return {
        data: [],
        total: response.nhits,
      }
    },
    async getAll(params) {
      const legacyFormat = false
      const order_by = ORDERBY.DESC
      const nresults = 15

      // If we have params and if there is no query or query is #all and there is no refinement
      if (
        params
        && params.community_id
        && (!params.query || params.query === '#all')
        && !params.refine?.length
      ) {
        const limitedResults = await shouldLimitExaleadResults({
          community_id: params.community_id,
          content_type: params.content_type || contentType,
        })

        // Set the limit if results are limited
        if (limitedResults) {
          // Create a date of previous year
          const date = new Date()
          date.setFullYear(date.getFullYear() - 1)

          // Convert to ISO string
          const isoString = date.toISOString()

          // Extend data and add the query
          params.query = `[ds6w:modified]>="${isoString}"`
        }
      }

      const response = await swymFetch
        .post(api, {
          json: {
            params: {
              legacyFormat,
              nresults,
              order_by,
              order_field: ORDERFIELD.UPDATE,
              query: '#all',
              start: 0,
              with_synthesis: !!params?.refine?.length, // disable tags fetch when no refine - Query should have better PCS
              ...(contentType ? { contentType } : {}),
              ...params,
            } as IExaleadParams,
          },
        })
        .json<ExaleadResponse>()

      return {
        total: response.nhits,
        groups: response.groups,
        data: parser(response),
      }
    },
    // A separate API for both groups and hits
    async getRawData(params = {}) {
    // If we have params and if there is no query or query is all and if we are sorted by latest contributions
    // and there is no refinement
      if (
        params.community_id
        && (!params.query || params.query === '#all')
        && params.order_field === ORDERFIELD.UPDATE
        && !params.refine?.length
      ) {
        const limitedResults = await shouldLimitExaleadResults({
          community_id: params.community_id,
          content_type: params.content_type || contentType,
        })

        // Set the limit if results are limited
        if (limitedResults) {
        // Create a date of previous year
          const date = new Date()
          date.setFullYear(date.getFullYear() - 1)

          // Convert to ISO string
          const isoString = date.toISOString()

          // Extend data and add the query
          params.query = `[ds6w:modified]>="${isoString}"`
        }
      }
      const response = await swymFetch
        .post(api, {
          json: {
            params: {
              legacyFormat: false,
              ...(contentType ? { contentType } : {}),
              query: '#all',
              with_synthesis: true, // mandatory to fetch tags
              order_by: ORDERBY.DESC,
              order_field: ORDERFIELD.UPDATE,
              ...params,
              start: 0, // force to fetch nothing but tags
              nresults: 0, // force to fetch nothing but tags
            } as IExaleadParams,
          },
        })
        .json<ExaleadResponse>()

      return {
        groups: response.groups,
        hits: response.hits,
        total: response.nhits,
      }
    },
  }
}

function getServices(): ContentExaleadServices<IEntity> {
  const contentTypes = [
    IContentModel.Post,
    IContentModel.Wedo,
    IContentModel.Conference,
    IContentModel.Survey,
    IContentModel.Wiki,
    IContentModel.Media,
    IContentModel.Idea,
    IContentModel.Wedo,
    IContentModel.Question,
  ]

  const adapter = {
    [IContentModel.Post]: ['posts', contentsParser, API.Post.Exalead.Content],
    [IContentModel.Media]: ['media', contentsParser, API.Media.Exalead.Content],
    [IContentModel.Idea]: ['idea', contentsParser, API.Idea.Exalead.Content],
    [IContentModel.Question]: ['iquestion', contentsParser, API.Question.Exalead.Content],
    [IContentModel.Wiki]: ['wikitree', contentsParser, API.Wiki.Exalead.Content],
    [IContentModel.Wedo]: ['wedo', contentsParser, API.Wedo.Exalead.Content],
    [IContentModel.Survey]: ['survey', contentsParser, API.Survey.Exalead.Content],
    [IContentModel.Conference]: ['conference', contentsParser, API.Conference.Exalead.Content],
  } as Record<IContentModel, [string, typeof contentsParser, typeof API.Post.Exalead.Content]>

  const services: ContentExaleadServices<IEntity> = contentTypes.reduce((acc, contentType) => {
    const [model, parser, api] = adapter[contentType]
    acc[contentType] = getService<IEntity>({ contentType: model, parser, api })
    return acc
  }, {} as ContentExaleadServices<IEntity>)

  return services
}

export default getServices()

export const getContentService = getService({
  parser: contentsParser,
  api: API.Post.Exalead.Content,
})

export const getExaleadDraftService = getService({
  parser: contentsParser,
  api: API.Draft.Exalead.Content,
})

export const getExaleadContributionService = getService({
  parser: contentsParser,
  api: API.Contribution.Exalead.Content,
})

export const getExaleadRitualService = getService({
  parser: contentsParser,
  api: API.Rituals.Exalead.Content,
})

/**
 * Search one element in exalead by id
 *
 * @param options Exalead api parameters
 * @param options.id query, eg: "buyg8457r7h757gerg"
 * @param options.derivedTypeId derived type id if the content is a derived type
 * @param options.contentType to filter on a content type
 * @returns
 *
 * @info NOT TO BE EXPORTED
 */
function _searchOneElementExalead(options: SearchOneElementExaleadOptions) {
  return cacheDecorator(
    `exalead-search-id-${options.id}`,
    (): Promise<IContent> => swymFetch
      .post('/api/exalead/search', {
        json: {
          params: {
            legacyFormat: false,
            query: `id:"${options.id}"`,
            nresults: 1,
            start: 0,
            with_synthesis: false,
            ...(options?.derivedTypeId ? { derived_type_id: options?.derivedTypeId } : undefined),
            ...(options?.contentType ? { contentType: options?.contentType } : undefined),
          },
        },
      })
      .json<ExaleadResponse>()
      .then(contentsParser)
      .then(response => response[0]),
    10000,
  )
}

/**
 * This query is built for searching content with only summary elements. It includes
 * - Author,
 * - Summary,
 * - Container info
 * - Title
 * - Model
 * @param query Full exalead query as string
 * @warn This will NOT work for derived type
 * @returns list of content matching the query
 */
export function searchElementsSummaryExalead(query: string) {
  return cacheDecorator(
    query,
    () => swymFetch
      .post('/api/exalead/search', {
        json: {
          params: {
            legacyFormat: false,
            query,
            select_predicate: [
              'ds6w:label', // content title
              'ds6w:type',
              'ds6w:resourceUid', // content uri
              'id', // content id
              'ds6w:community', // content community title
              'community_type', // content community type
              'ds6w:containerUid', // content community subject uri
              'ds6w:docType',
              'ds6w:typeId',
              'ds6w:kind',
              'ds6w:typeFontIcon',
              'ds6w:typeColor',
              'enhanced_summary', // content summary
              'swym_source', // content model
              'media_type',
              'username', // 'author_uid',
              'user_firstname',
              'user_lastname',
              'update', // Last updated date
            ],
            nresults: 50,
            start: 0,
            with_synthesis: false,
          },
        },
      })
      .json<ExaleadResponse>()
      .then(contentsParser),
    10000,
  )
}

/**
 * This query is built for searching content with only summary elements.
 * Calls will be mutualized
 * It includes
 * - Author,
 * - Summary,
 * - Container info
 * - Title
 * - Model
 *
 * @param options
 * @param options.id content id
 * @param options.derivedTypeId content derived type /!\ NOT USED ATM
 * @param options.contentType content type (idea, post, etc..)
 *
 * @warn This will NOT work for derived type
 *
 * @returns content matching the query
 */
export const searchOneElementMutualized = mutualizerDecorator<
  typeof searchElementsSummaryExalead,
  SearchOneElementExaleadOptions,
  IContent
>({
  fetch: searchElementsSummaryExalead,
  merge: bucket => createExaleadQueryParameters(bucket.map(el => el.contentType) as string[], bucket.map(el => el.id)),
  // Could resolve different content with same id
  resolve: ([response, [option, resolve]]) => resolve(response.find(el => el.id === option.id) as IContent),
  options: { limit: 50 },
})

export function searchOneElementExalead(options: SearchOneElementExaleadOptions) {
  if (options.derivedTypeId)
    return _searchOneElementExalead(options)

  return searchOneElementMutualized(options) as unknown as Promise<IContent>
}

/**
 * Search one or many elements in exalead
 * @param query user query, eg: "[ds6w:resourceUid]:(6g49zehj7t864)" or "foo OR bar"
 * @param withDraft Set true to query on draft content
 * @param withSubTypes
 * @returns
 */
export function searchElementsContentExalead(
  query: string,
  withDraft = false,
  withSubTypes = false,
) {
  return swymFetch
    .post('/api/exalead/search', {
      json: {
        params: {
          legacyFormat: false,
          query,
          nresults: 20,
          start: 0,
          with_synthesis: false,
          withDraft,
          ...(withSubTypes ? { with_subtype: withSubTypes } : undefined),
        },
      },
    })
    .json<ExaleadResponse>()
    .then(contentsParser)
}

/**
 * Search one or many elements in exalead by resourceUid (= subjectUri)
 * @param query resourceUid query, eg: "buyg8457r7h757gerg" or "1fz86g45egzg OR g4e84g6e8g48g6z"
 * @param withDraft Set true to query on draft content
 * @param withSubTypes
 * @returns
 */
export function searchElementsExalead(
  query: string,
  withDraft = false,
  withSubTypes = false,
) {
  return searchElementsContentExalead(
    `[ds6w:resourceUid]:(${query})`,
    withDraft,
    withSubTypes,
  )
}
